import React from 'react';
import { X, Wrench, Network, Zap, Shield, UserPlus, UserMinus } from 'lucide-react';
import { StaffMember, StaffRole } from '../types';
import { STAFF_ROLE_CONFIGS, STAFF_HIRE_SIGNING_BONUS_MONTHS } from '../constants';

interface StaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  staff: StaffMember[];
  money: number;
  hireStaff: (role: StaffRole) => boolean;
  fireStaff: (id: string) => void;
  getDailyStaffCost: () => number;
  formatCurrency: (amount: number, decimals?: number) => string;
}

const ROLE_ICONS: Record<StaffRole, React.ReactNode> = {
  technician: <Wrench className="w-5 h-5 text-orange-400" />,
  network_engineer: <Network className="w-5 h-5 text-blue-400" />,
  electrician: <Zap className="w-5 h-5 text-yellow-400" />,
  security_officer: <Shield className="w-5 h-5 text-green-400" />,
};

const ROLE_COLORS: Record<StaffRole, string> = {
  technician: 'border-orange-600/40 bg-orange-900/20',
  network_engineer: 'border-blue-600/40 bg-blue-900/20',
  electrician: 'border-yellow-600/40 bg-yellow-900/20',
  security_officer: 'border-green-600/40 bg-green-900/20',
};

const StaffModal: React.FC<StaffModalProps> = ({
  isOpen,
  onClose,
  staff,
  money,
  hireStaff,
  fireStaff,
  getDailyStaffCost,
  formatCurrency,
}) => {
  if (!isOpen) return null;

  const dailyCost = getDailyStaffCost();
  const monthlyCost = dailyCost * 30;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-white">Staff Management</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Monthly payroll: <span className="text-orange-400 font-semibold">{formatCurrency(monthlyCost)}/month</span>
              <span className="ml-2 text-gray-500">({formatCurrency(dailyCost)}/day)</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {STAFF_ROLE_CONFIGS.map(config => {
            const hired = staff.filter(s => s.role === config.role);
            const count = hired.length;
            const signingBonus = config.monthlySalary * STAFF_HIRE_SIGNING_BONUS_MONTHS;
            const canAfford = money >= signingBonus;
            const atMax = count >= config.maxCount;

            return (
              <div key={config.role} className={`rounded-lg border p-4 ${ROLE_COLORS[config.role]}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    {ROLE_ICONS[config.role]}
                    <div>
                      <div className="font-semibold text-white">{config.name}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{config.description}</div>
                      <div className="text-xs text-emerald-400 mt-1">{config.effect}</div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <div className="text-sm text-gray-300">
                      <span className="font-semibold text-white">{count}</span>
                      <span className="text-gray-500">/{config.maxCount}</span>
                    </div>
                    <div className="text-xs text-gray-400">{formatCurrency(config.monthlySalary)}/mo each</div>
                    <button
                      onClick={() => hireStaff(config.role)}
                      disabled={atMax || !canAfford}
                      title={atMax ? 'Maximum staff reached' : !canAfford ? `Need ${formatCurrency(signingBonus)} signing bonus` : `Hire (${formatCurrency(signingBonus)} signing bonus)`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white"
                    >
                      <UserPlus size={13} />
                      Hire ({formatCurrency(signingBonus)})
                    </button>
                  </div>
                </div>

                {/* Hired members */}
                {hired.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-700 space-y-1">
                    {hired.map(member => (
                      <div key={member.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-300">{member.name}</span>
                        <button
                          onClick={() => fireStaff(member.id)}
                          className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 px-2 py-0.5 rounded hover:bg-red-900/30"
                        >
                          <UserMinus size={12} />
                          Fire
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StaffModal;
